import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AppIcon from '../AppIcon';

const Footer = () => {
  const [showPB, setShowPB] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined" || typeof fetch === "undefined") return;
    fetch("/site-config.json?_=" + Date.now(), { cache: "no-store" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && typeof d.showPoweredBy === "boolean") setShowPB(d.showPoweredBy); })
      .catch(() => {});
  }, []);

  const currentYear = new Date()?.getFullYear();

  const footerLinks = {
    shop: [
      { label: 'All Products', path: '/product-catalog' },
      { label: 'New Arrivals', path: '/product-catalog?filter=new' },
      { label: 'Best Sellers', path: '/product-catalog?filter=bestseller' },
      { label: 'Sale', path: '/product-catalog?filter=sale' }
    ],
    account: [
      { label: 'My Account', path: '/user-account-dashboard' },
      { label: 'Order History', path: '/user-account-dashboard?tab=orders' },
      { label: 'Wishlist', path: '/user-account-dashboard?tab=wishlist' },
      { label: 'Sign In', path: '/user-login' }
    ],
    support: [
      { label: 'Contact Us', path: '#contact' },
      { label: 'Shipping Info', path: '#shipping' },
      { label: 'Returns', path: '#returns' },
      { label: 'FAQ', path: '#faq' }
    ],
    company: [
      { label: 'About Us', path: '#about' },
      { label: 'Careers', path: '#careers' },
      { label: 'Privacy Policy', path: '#privacy' },
      { label: 'Terms of Service', path: '#terms' }
    ]
  };

  const socialLinks = [
    { name: 'Facebook', icon: 'Facebook', url: '#facebook' },
    { name: 'Instagram', icon: 'Instagram', url: '#instagram' },
    { name: 'Twitter', icon: 'Twitter', url: '#twitter' },
    { name: 'Youtube', icon: 'Youtube', url: '#youtube' }
  ];

  return (
    <footer className="bg-gray-900 border-t border-gray-800 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <img src="/logo.png" alt="Bounderlaster" className="w-9 h-9 object-cover rounded-full border border-white/30" />
              <span className="text-xl font-heading font-semibold text-white" data-ocwp-field="brand">
                Bounderlaster
              </span>
            </Link>
            <p data-ocwp-field="footerTagline" className="text-sm text-gray-400 mb-4">
              Your trusted destination for premium beauty products from top brands worldwide.
            </p>
            <div className="flex gap-4">
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-800 text-gray-400 hover:bg-primary hover:text-primary-foreground 
                           flex items-center justify-center transition-colors duration-200"
                  aria-label={social?.name}
                >
                  <AppIcon name={social?.icon} className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Shop
            </h3>
            <ul className="space-y-3">
              {footerLinks?.shop?.map((link) => (
                <li key={link?.path}>
                  <Link
                    to={link?.path}
                    className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Account Links */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Account
            </h3>
            <ul className="space-y-3">
              {footerLinks?.account?.map((link) => (
                <li key={link?.path}>
                  <Link
                    to={link?.path}
                    className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Support
            </h3>
            <ul className="space-y-3">
              {footerLinks?.support?.map((link) => (
                <li key={link?.path}>
                  <a
                    href={link?.path}
                    className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {link?.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks?.company?.map((link) => (
                <li key={link?.path}>
                  <a
                    href={link?.path}
                    className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {link?.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="py-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 max-w-md">
              <h3 className="text-sm font-semibold text-white mb-2">
                Subscribe to our newsletter
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                Get exclusive offers and beauty tips delivered to your inbox.
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-2 text-sm bg-gray-800 border border-gray-700 text-white rounded-md 
                           focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent placeholder-gray-500"
                />
                <button
                  className="px-6 py-2 bg-primary text-primary-foreground text-sm font-medium 
                           rounded-md hover:bg-primary/90 transition-colors duration-200"
                >
                  Subscribe
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-400">We accept:</span>
              <div className="flex gap-3">
                <AppIcon name="CreditCard" className="w-8 h-5 text-gray-400" />
                <AppIcon name="Wallet" className="w-8 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-gray-800">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-sm text-gray-400">
              © {currentYear} <span data-ocwp-field="brand">Bounderlaster</span>. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a
                href="#privacy"
                className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
              >
                Privacy Policy
              </a>
              <a
                href="#terms"
                className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
              >
                Terms of Service
              </a>
              <a
                href="#cookies"
                className="text-sm text-gray-400 hover:text-white transition-colors duration-200"
              >
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Powered-by strip — separate self-contained div at the very bottom.
          Hiding it (toggle off) removes one element cleanly, no layout shift.
          Default ON via nullish coalescing; the dashboard toggle will inject
          `window.__OH_SITE_CONFIG.showPoweredBy = false` to hide. */}
      {showPB && (
        <div className="bg-gray-900 py-4 text-center">
          <p className="text-xs text-gray-500 italic">
            Powered by{" "}
            <a href="https://offloadhost.com/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-200 underline decoration-dotted">
              OffloadHost
            </a>
          </p>
        </div>
      )}
    </footer>
  );
};

export default Footer;